import type { DraftRight, DraftType } from './drafts';
import { NamedEntityMinDto } from './other';

export enum NotificationCategory {
    CASE_AND_CLIENT = 'CASE_AND_CLIENT',
    COMPLIANCE = 'COMPLIANCE',
    CORRESPONDENCE = 'CORRESPONDENCE',
    DEADLINES = 'DEADLINES',
    DRAFTS = 'DRAFTS',
    INVOICING = 'INVOICING',
    TASKS = 'TASKS',
}

export enum NotificationActionType {
    OPEN_PARTY_INVOICE_CR = 'OPEN_PARTY_INVOICE_CR',
}

// Not called Notification to prevent overlap with a global type
export interface AutomatedNotification {
    id: number;
    title: string;
    content: string;
    category: NotificationCategory | string;
    isArchived: boolean;
    isImportant: boolean;
    createdAt: string;
    readAt: string | null;
    metaDataJson: MetaData;
}

type MetaData = {
    action?: string;
    party?: NamedEntityMinDto;
    matter?: { id: number; fullName: string; isConfidential?: boolean | null };
    contractSentAt?: string;
    invoice?: { id: number; invoiceNumber: string; invoiceStatusTypeCode: string };
    /** The invoice comment a comment notification opens. */
    commentId?: number;
    employee?: NamedEntityMinDto;
    contract?: NamedEntityMinDto;
    invoiceTransition?: {
        id: number;
        comment: string;
        changedByEmployee: NamedEntityMinDto;
        invoiceTransitionActionType: string;
    };
    threshold?: number | string;
    billing?: {
        fixedFee: number;
        capLimit?: number;
        supervisedByEmployee: NamedEntityMinDto;
        responsibleEmployee: NamedEntityMinDto | null;
        currency: string;
    };
    project?: NamedEntityMinDto;
    parties?: NamedEntityMinDto[];
    // Delegation notifications: the nested object only carries the id used for the delegation link;
    // all display values (delegator, substitute, caseCount label, dates, cases, role) are top-level.
    delegation?: { id: number };
    delegator?: NamedEntityMinDto;
    substitute?: NamedEntityMinDto;
    /** Who acted for the recipient, or for their substitute. */
    coordinator?: NamedEntityMinDto;
    /** A coordinator's copy of an alert the delegator got. */
    forCoordinator?: boolean;
    /** Who sent a reminder. */
    sender?: NamedEntityMinDto;
    substitutes?: NamedEntityMinDto[];
    /** Pre-formatted count label, e.g. "1 case" / "3 cases". */
    caseCount?: string;
    /** The case count as a number, for the plural forms. */
    count?: number;
    /** Working days until the absence starts; 0 once it has started, or when none is left before the start. */
    workingDays?: number;
    /** With workingDays 0: whether the start date has come. */
    started?: boolean;
    /** ISO yyyy-MM-dd. */
    startDate?: string;
    /** ISO yyyy-MM-dd. */
    endDate?: string;
    /** Affected matters; name is "caseNumber name", rendered as links to the matter. */
    cases?: { id: number; name: string; isConfidential?: boolean | null }[];
    role?: 'SP' | 'RL' | 'SP / RL';
    /** ISO yyyy-MM-dd date the cases were automatically returned. */
    returnedOn?: string;
    /** The substitutes who have not answered, each with their case count, as one pre-formatted list. */
    pendingSubstitutes?: string;
    /** The daily summary: a line per changed delegation, its changes worded by the backend. */
    summary?: {
        delegation: { id: number };
        delegator: NamedEntityMinDto;
        /** ISO yyyy-MM-dd. */
        startDate: string;
        /** ISO yyyy-MM-dd. */
        endDate: string;
        changes: string;
    }[];
    correspondence?: CorrespondenceMetaDataDto;
    task?: NamedEntityMinDto;
    date?: string;
    days?: number;
    openedDate?: string;
    reasons?: string;
    amlExtensionDate?: string;
    /** Compliance check expiry notifications; the backend stores the day count as a string. */
    checkTypeName?: string;
    partyName?: string;
    daysUntilExpiry?: string;
    employees?: NamedEntityMinDto[];
    remainingAmount?: string;
    remainingAmountCurrency?: string;
    totalAmount?: string;
    totalAmountCurrency?: string;
    currentPartners?: NamedEntityMinDto[];
    previousPartners?: NamedEntityMinDto[];
    currentManagers?: NamedEntityMinDto[];
    previousManagers?: NamedEntityMinDto[];
    /** Draft notifications: the record, by its create type and its id, and the name it had then. */
    draft?: { type: DraftType; id: number; name: string | null };
    /** Who shared or completed the draft. */
    actor?: NamedEntityMinDto;
    /** Shared: the highest right the recipient now holds, and the labels still to fill. */
    right?: DraftRight;
    missingFields?: string[];
    /** Completed: the new record's number; null for a record that numbers nothing. */
    number?: string | null;
    /** Expiring: ISO yyyy-MM-dd. */
    lastSavedOn?: string;
    deletesOn?: string;
};

interface CorrespondenceMetaDataDto {
    id: number;
    number: string;
    name: string;
}
